import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, Typography, Box, Paper, Button, Chip, 
  Divider, Avatar, Grid, CircularProgress, Alert 
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PublicIcon from '@mui/icons-material/Public';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { gnewsService } from '../api/services';
import AdBanner from '../components/Ads/AdBanner';

const NewsDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        setLoading(true);
        // Fetch all news and find by ID (since we don't have a direct retrieve endpoint yet, 
        // or we can use the list endpoint filtering if available. 
        // For now, let's assume we can fetch by ID or filter from a list. 
        // Given the current API, we might need to fetch all and filter client-side 
        // OR implement a retrieve endpoint. 
        // Let's try to fetch from the 'all news' endpoint filtering by ID if possible, 
        // or just fetch trending/category and find it.
        // Ideally, backend should have a retrieve endpoint. 
        // Let's assume we can get it from the list for now or implement retrieve in backend.
        // Wait, the plan said "Fetches article by ID from /api/external-news/:id/".
        // I need to make sure that endpoint exists. 
        // The ViewSet is ReadOnlyModelViewSet, so it DOES have retrieve by default!
        
        const res = await gnewsService.getArticleById(id);
        setArticle(res.data);
      } catch (err) {
        console.error("Error fetching article:", err);
        setError("No se pudo cargar la noticia. Puede que haya expirado o no exista.");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchArticle();
    }
  }, [id]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !article) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={() => navigate('/')}>
            Volver al Inicio
          </Button>
        }>
          {error || "Noticia no encontrada"}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button 
        startIcon={<ArrowBackIcon />} 
        onClick={() => navigate('/')}
        sx={{ mb: 3 }}
      >
        Volver a Noticias
      </Button>

      {/* Top Ad Banner */}
      <Box sx={{ my: 3 }}>
        <AdBanner format="horizontal" placement="news_detail_top" />
      </Box>

      <Paper elevation={0} sx={{ p: { xs: 2, md: 6 }, borderRadius: 4, bgcolor: 'background.paper' }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Chip 
              label={article.source_name} 
              icon={<PublicIcon fontSize="small" />} 
              color="primary" 
              variant="outlined" 
            />
            <Chip 
              label={new Date(article.published_at).toLocaleDateString('es-VE', { 
                year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' 
              })} 
              icon={<CalendarTodayIcon fontSize="small" />} 
              variant="outlined" 
            />
          </Box>

          <Typography variant="h3" component="h1" fontWeight="800" gutterBottom sx={{ fontSize: { xs: '2rem', md: '3rem' } }}>
            {article.title}
          </Typography>
        </Box>

        {/* Featured Image */}
        {article.image_with_placeholder && (
          <Box sx={{ 
            position: 'relative',
            width: '100%', 
            height: { xs: 300, md: 500 },
            overflow: 'hidden',
            borderRadius: 4,
            mb: 4,
            backgroundColor: 'background.default'
          }}>
            {/* Background layer - blurred */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `url(${article.image_with_placeholder})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                filter: 'blur(20px) brightness(0.8)',
                transform: 'scale(1.1)',
              }}
            />
            
            {/* Foreground image - sharp, contained */}
            <Box
              component="img"
              src={article.image_with_placeholder}
              alt={article.title}
              sx={{ 
                position: 'relative',
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                zIndex: 1,
              }}
            />
          </Box>
        )}

        {/* Content */}
        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Typography variant="h6" paragraph sx={{ fontWeight: 600, color: 'text.secondary', mb: 3 }}>
              {article.description}
            </Typography>
            
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
              {article.content || article.description}
            </Typography>

            <Box sx={{ mt: 6, p: 4, bgcolor: 'action.hover', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                ¿Quieres leer la historia completa?
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Este es un resumen proporcionado por nuestra fuente de noticias. Puedes leer el artículo completo en su sitio web original.
              </Typography>
              <Button 
                variant="contained" 
                endIcon={<OpenInNewIcon />}
                href={article.url}
                target="_blank"
                rel="noopener noreferrer"
                size="large"
                sx={{ borderRadius: 50, px: 4 }}
              >
                Leer en {article.source_name}
              </Button>
            </Box>

            {/* Mid-content Ad */}
            <Box sx={{ my: 4 }}>
              <AdBanner format="horizontal" placement="news_detail_mid" />
            </Box>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            <Box sx={{ position: 'sticky', top: 100 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Fuente
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
                 <Avatar variant="rounded" sx={{ bgcolor: 'primary.main' }}>
                    {article.source_name[0]}
                 </Avatar>
                 <Box>
                   <Typography variant="subtitle1" fontWeight="bold">
                     {article.source_name}
                   </Typography>
                   <Typography variant="caption" color="text.secondary">
                     Fuente Verificada
                   </Typography>
                 </Box>
               </Paper>

               {/* Sidebar Ad */}
               <Box sx={{ mt: 3 }}>
                 <AdBanner format="rectangle" placement="news_detail_sidebar" />
               </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Bottom Ad Banner */}
        <Box sx={{ mt: 6 }}>
          <AdBanner format="horizontal" placement="news_detail_bottom" />
        </Box>
      </Paper>
    </Container>
  );
};

export default NewsDetail;
